import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';

function formatNumber(n: number): string { return n.toLocaleString('pt-BR'); }

export default function MyUsagePage() {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [contactsCount, setContactsCount] = useState(0);
  const [campaignsCount, setCampaignsCount] = useState(0);
  const [whatsappsCount, setWhatsappsCount] = useState(0);
  const [diskUsage, setDiskUsage] = useState({ used: 0, total: 1 });

  const monthlyLimit = profile?.monthly_message_limit || 5000;
  const sentThisMonth = profile?.messages_sent_this_month || 0;
  const usagePercent = monthlyLimit > 0 ? Math.min(100, Math.round((sentThisMonth / monthlyLimit) * 100)) : 0;
  const remaining = Math.max(0, monthlyLimit - sentThisMonth);
  const currentDay = new Date().getDate();
  const daysInMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
  const dailyAverage = currentDay > 0 ? Math.round(sentThisMonth / currentDay) : 0;
  const projectedUsage = dailyAverage * daysInMonth;
  const willExceedLimit = projectedUsage > monthlyLimit;

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        if (profile?.company_id) {
          const [{ count: cc }, { count: cmp }, { count: wh }] = await Promise.all([
            supabase.from('contacts').select('*', { count: 'exact', head: true }).eq('company_id', profile.company_id),
            supabase.from('campaigns').select('*', { count: 'exact', head: true }).eq('company_id', profile.company_id),
            supabase.from('evolution_instances').select('*', { count: 'exact', head: true }).eq('company_id', profile.company_id),
          ]);
          setContactsCount(cc || 0);
          setCampaignsCount(cmp || 0);
          setWhatsappsCount(wh || 0);
        }
      } catch { /* ignore */ }
      setLoading(false);
    };
    fetchCounts();
  }, [profile?.company_id]);

  const alertLevel = useMemo(() => {
    if (sentThisMonth >= monthlyLimit) return 'exceeded';
    if (usagePercent >= 90) return 'critical';
    if (usagePercent >= 70) return 'warning';
    return 'normal';
  }, [usagePercent, sentThisMonth, monthlyLimit]);

  const alertConfig = {
    normal: { bg: 'bg-background-50', border: 'border-background-200', text: 'text-emerald-700', icon: 'ri-check-double-line', iconBg: 'bg-emerald-50', message: 'Você está dentro do limite do seu plano.' },
    warning: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: 'ri-alert-line', iconBg: 'bg-amber-50', message: 'Atenção! Você já usou mais de 70% do limite mensal.' },
    critical: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', icon: 'ri-error-warning-line', iconBg: 'bg-orange-50', message: 'Crítico! Menos de 10% do limite disponível.' },
    exceeded: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: 'ri-close-circle-line', iconBg: 'bg-red-50', message: 'Limite mensal atingido! Seus envios estão bloqueados.' },
  }[alertLevel];

  const progressBarColor = usagePercent > 90 ? 'bg-red-500' : usagePercent > 70 ? 'bg-amber-500' : 'bg-emerald-500';

  return (
    <div className="max-w-5xl mx-auto space-y-7">
      <div>
        <h1 className="text-2xl font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-2">Meu Uso</h1>
        <p className="text-sm text-foreground-500 dark:text-foreground-400">Acompanhe em tempo real o consumo de recursos do seu plano</p>
      </div>

      {/* Resource cards */}
      {!loading && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-4 text-center">
            <div className="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center mx-auto mb-3">
              <i className="ri-send-plane-line text-lg text-primary-600 dark:text-primary-400" />
            </div>
            <p className="text-xl font-bold text-foreground-950 dark:text-foreground-100">{formatNumber(sentThisMonth)}</p>
            <p className="text-[11px] text-foreground-500 mt-0.5">Mensagens</p>
          </div>
          <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-4 text-center">
            <div className="w-10 h-10 rounded-xl bg-green-50 dark:bg-green-500/10 flex items-center justify-center mx-auto mb-3">
              <i className="ri-whatsapp-line text-lg text-green-600 dark:text-green-400" />
            </div>
            <p className="text-xl font-bold text-foreground-950 dark:text-foreground-100">{whatsappsCount}</p>
            <p className="text-[11px] text-foreground-500 mt-0.5">WhatsApps</p>
          </div>
          <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-4 text-center">
            <div className="w-10 h-10 rounded-xl bg-accent-50 dark:bg-accent-500/10 flex items-center justify-center mx-auto mb-3">
              <i className="ri-contacts-book-line text-lg text-accent-600 dark:text-accent-400" />
            </div>
            <p className="text-xl font-bold text-foreground-950 dark:text-foreground-100">{formatNumber(contactsCount)}</p>
            <p className="text-[11px] text-foreground-500 mt-0.5">Contatos</p>
          </div>
          <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-4 text-center">
            <div className="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center mx-auto mb-3">
              <i className="ri-megaphone-line text-lg text-amber-600 dark:text-amber-400" />
            </div>
            <p className="text-xl font-bold text-foreground-950 dark:text-foreground-100">{campaignsCount}</p>
            <p className="text-[11px] text-foreground-500 mt-0.5">Campanhas</p>
          </div>
        </div>
      )}

      {/* Alert */}
      {alertLevel !== 'normal' && (
        <div className={`${alertConfig.bg} border ${alertConfig.border} rounded-2xl p-5 flex items-start gap-4`}>
          <div className={`w-11 h-11 rounded-xl ${alertConfig.iconBg} flex items-center justify-center flex-shrink-0`}>
            <i className={`${alertConfig.icon} text-xl ${alertConfig.text}`} />
          </div>
          <div className="flex-1">
            <p className={`text-sm font-semibold ${alertConfig.text} mb-1`}>{alertLevel === 'exceeded' ? 'Limite Atingido' : alertLevel === 'critical' ? 'Crítico' : 'Atenção'}</p>
            <p className="text-xs text-foreground-600 leading-relaxed">{alertConfig.message}</p>
            <Link to="/subscription" className="inline-flex items-center gap-1.5 mt-2 px-4 py-1.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-add-circle-line" />Comprar mensagens extras
            </Link>
          </div>
        </div>
      )}

      {/* Main progress card */}
      <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-base font-heading font-semibold text-foreground-950 dark:text-foreground-100 mb-1">Consumo Mensal</h2>
            <p className="text-xs text-foreground-500">Plano atual: <span className="font-medium capitalize">{profile?.plan || 'Starter'}</span></p>
          </div>
          <div className="text-right">
            <p className={`text-sm font-semibold ${alertLevel === 'exceeded' ? 'text-red-600' : 'text-foreground-700 dark:text-foreground-300'}`}>{formatNumber(sentThisMonth)} / {formatNumber(monthlyLimit)}</p>
            <p className="text-xs text-foreground-500">mensagens este mês</p>
          </div>
        </div>

        <div className="relative mb-4">
          <div className="w-full h-5 bg-background-100 dark:bg-background-200 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-700 ease-out ${progressBarColor}`} style={{ width: `${usagePercent}%` }} />
          </div>
        </div>

        <div className="flex justify-between text-[10px] text-foreground-400 mb-3">
          <span>0</span><span>{formatNumber(Math.round(monthlyLimit * 0.25))}</span><span>{formatNumber(Math.round(monthlyLimit * 0.5))}</span><span>{formatNumber(Math.round(monthlyLimit * 0.75))}</span><span>{formatNumber(monthlyLimit)}</span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-5 border-t border-background-100 dark:border-background-200">
          <div className="text-center p-3 rounded-xl bg-background-50 dark:bg-background-200"><p className="text-lg font-heading font-bold text-foreground-950 dark:text-foreground-100">{formatNumber(remaining)}</p><p className="text-[10px] text-foreground-500">Restantes</p></div>
          <div className="text-center p-3 rounded-xl bg-background-50 dark:bg-background-200"><p className="text-lg font-heading font-bold text-foreground-950 dark:text-foreground-100">{usagePercent}%</p><p className="text-[10px] text-foreground-500">Utilizado</p></div>
          <div className="text-center p-3 rounded-xl bg-background-50 dark:bg-background-200"><p className="text-lg font-heading font-bold text-foreground-950 dark:text-foreground-100">{formatNumber(dailyAverage)}</p><p className="text-[10px] text-foreground-500">Média/dia</p></div>
          <div className={`text-center p-3 rounded-xl ${willExceedLimit ? 'bg-red-50' : 'bg-background-50 dark:bg-background-200'}`}><p className={`text-lg font-heading font-bold ${willExceedLimit ? 'text-red-600' : 'text-foreground-950 dark:text-foreground-100'}`}>{formatNumber(projectedUsage)}</p><p className={`text-[10px] ${willExceedLimit ? 'text-red-500' : 'text-foreground-500'}`}>Projeção</p></div>
        </div>
      </div>

      {/* Plan Limits */}
      <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-6">
        <h2 className="text-base font-heading font-semibold text-foreground-950 dark:text-foreground-100 mb-5">Limites do Plano</h2>
        <div className="space-y-4">
          {[
            { label: 'Mensagens', used: sentThisMonth, total: monthlyLimit, icon: 'ri-send-plane-line', color: 'bg-primary-500' },
            { label: 'WhatsApps', used: whatsappsCount, total: profile?.plan === 'enterprise' ? 50 : profile?.plan === 'business' ? 20 : profile?.plan === 'professional' ? 10 : 5, icon: 'ri-whatsapp-line', color: 'bg-green-500' },
            { label: 'Contatos', used: contactsCount, total: profile?.plan === 'enterprise' ? 500000 : profile?.plan === 'business' ? 100000 : profile?.plan === 'professional' ? 50000 : 10000, icon: 'ri-contacts-book-line', color: 'bg-accent-500' },
            { label: 'Campanhas', used: campaignsCount, total: profile?.plan === 'enterprise' ? 200 : profile?.plan === 'business' ? 100 : profile?.plan === 'professional' ? 50 : 20, icon: 'ri-megaphone-line', color: 'bg-amber-500' },
            { label: 'Espaço', used: 128, total: profile?.plan === 'enterprise' ? 5120 : profile?.plan === 'business' ? 1024 : 512, unit: 'MB', icon: 'ri-hard-drive-2-line', color: 'bg-secondary-500' },
          ].map((item) => {
            const pct = item.total > 0 ? Math.min(100, Math.round((item.used / item.total) * 100)) : 0;
            return (
              <div key={item.label}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <i className={`${item.icon} text-foreground-500 text-sm`} />
                    <span className="text-xs font-medium text-foreground-700">{item.label}</span>
                  </div>
                  <span className="text-xs font-semibold text-foreground-900">{formatNumber(item.used)}{item.unit ? ` ${item.unit}` : ''} / {formatNumber(item.total)}{item.unit ? ` ${item.unit}` : ''} <span className="text-foreground-400 font-normal">({pct}%)</span></span>
                </div>
                <div className="w-full h-2 bg-background-100 dark:bg-background-200 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all duration-500 ${item.color}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Link to="/subscription" className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 p-5 hover:border-primary-300 transition-all group">
          <div className="w-10 h-10 rounded-xl bg-accent-50 dark:bg-accent-500/10 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
            <i className="ri-add-circle-line text-xl text-accent-600 dark:text-accent-400" />
          </div>
          <h3 className="text-sm font-semibold text-foreground-950 mb-1">Comprar Excedente</h3>
          <p className="text-xs text-foreground-500">Adicione mensagens extras</p>
        </Link>
        <Link to="/subscription" className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 p-5 hover:border-primary-300 transition-all group">
          <div className="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
            <i className="ri-arrow-up-circle-line text-xl text-primary-600 dark:text-primary-400" />
          </div>
          <h3 className="text-sm font-semibold text-foreground-950 mb-1">Fazer Upgrade</h3>
          <p className="text-xs text-foreground-500">Mude para um plano superior</p>
        </Link>
        <Link to="/setup-fee" className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 p-5 hover:border-primary-300 transition-all group">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
            <i className="ri-tools-line text-xl text-emerald-600 dark:text-emerald-400" />
          </div>
          <h3 className="text-sm font-semibold text-foreground-950 mb-1">Implantação</h3>
          <p className="text-xs text-foreground-500">Configuramos tudo pra você</p>
        </Link>
      </div>
    </div>
  );
}